import { AccredibleUser } from '@accredible-frontend-v2/models';
import { AccountsRedirectionKey } from '@accredible-frontend-v2/services/accounts-redirection';
import { AccredibleLanguageService } from '@accredible-frontend-v2/services/language';
import { AccredibleUrlHelper } from '@accredible-frontend-v2/utils/url-helper';
import { DOCUMENT } from '@angular/common';
import { inject, Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as AuthorizedActions from '../../../core/stores/authorized/authorized.actions';
import { AuthorizedState } from '../../../core/stores/authorized/authorized.reducer';
import { SmartResumeLoginService } from '../../external/smart-resume/smartresume-login.service';

export enum App {
  SPOTLIGHT = 'spotlight',
  RECIPIENT_PORTAL = 'recipient-portal',
  COURSEFINDER = 'coursefinder',
  SMARTRESUME = 'smartresume',
}

@Injectable({
  providedIn: 'root',
})
export class AccredibleLoginService {
  private readonly _route = inject(ActivatedRoute);
  private readonly _language = inject(AccredibleLanguageService);
  private readonly _smartResume = inject(SmartResumeLoginService);
  private readonly _router = inject(Router);
  private readonly _authorizedStore = inject(Store<AuthorizedState>);

  constructor(@Inject(DOCUMENT) private readonly _document: Document) {
    this._document = inject(DOCUMENT);
  }

  loginRedirectionHandler(loginData: { user: AccredibleUser; sessionToken: string }): void {
    // User is logged in
    this._authorizedStore.dispatch(AuthorizedActions.setUser({ user: loginData.user }));

    const { origin, app } = this._route.snapshot.queryParams;

    if (origin && !origin.includes(this._document.location.host)) {
      switch (app) {
        case App.RECIPIENT_PORTAL:
          if (this._shouldGoToWallet(origin)) {
            // If the origin is the Recipient Portal welcome page
            const url = AccredibleUrlHelper.createUrl(origin, 'Login origin url');
            this._document.location.href = `${url.origin}/profile/${
              loginData.user.username
            }/wallet?language=${this._language.getSelectedLanguage().code}&token=${
              loginData.sessionToken
            }`;
          } else {
            // If the origin is the Recipient Portal but not the welcome page
            const url = new URL(origin);
            url.searchParams.set('token', loginData.sessionToken);
            url.searchParams.set(
              AccountsRedirectionKey.LANGUAGE,
              this._language.getSelectedLanguage().code,
            );
            this._document.location.href = url.href;
          }
          break;

        case App.SPOTLIGHT:
          this._router.navigate(['settings/directory-profiles']).then();
          break;

        case App.COURSEFINDER:
          const url = new URL(origin);
          url.searchParams.set('token', loginData.sessionToken);
          this._document.location.href = url.href;
          break;

        default:
          this._setLanguageQueryParam(loginData.sessionToken);
      }
    } else {
      if (app === App.SMARTRESUME) {
        const { user, sessionToken } = loginData;
        this._smartResume.redirectToCredentialShare(user.username, sessionToken);
        return;
      }

      this._router.navigate(['settings']).then();
    }
  }

  private _setLanguageQueryParam(sessionToken: string): void {
    const url = new URL(this._document.location.href);
    url.searchParams.set('language', this._language.getSelectedLanguage().code);
    url.searchParams.set('token', sessionToken);

    this._document.location.href = url.href;
  }

  /**
   * This method evaluates if the user should be redirected to the wallet or not.
   * If he comes from the welcome page from credential.net he should go to the wallet.
   * TODO(Fred): Maybe we will need to add more routes here in the future or make the logic a little bit clearer.
   */
  private _shouldGoToWallet(origin: string): boolean {
    const goToWalletRoutes = ['/welcome'];
    for (let i = 0; i < goToWalletRoutes.length; i++) {
      if (origin.indexOf(goToWalletRoutes[i]) > -1) {
        return true;
      }
    }
    return false;
  }
}
